import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { useToast } from '../ui/use-toast';
import { Plus, Edit, Trash2, Search, ChevronDown, ChevronUp, ChevronsUpDown, UtensilsCrossed, Coffee, PlusCircle, Package, Power, PowerOff, ShoppingBag, Croissant, Wine, MapPin, Eye } from 'lucide-react';
import { getDishes, deleteDish, setDishActive, setDishLocationAvailability, setDishLocationAvailabilityBulk } from '../../services/menuService';
import { confirm } from '../ui/confirm-dialog';
import {
  Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription,
} from '../ui/dialog';
import { formatUnitLabel } from '../../constants/dishes';
import { useAppSettings } from '../../context/AppSettingsContext';
import { useRestaurant } from '../../context/RestaurantContext';
import { useAuth } from '../../context/AuthContext';
import { useT } from '../../context/LanguageContext';
import { getRestaurantFromStorage } from '../../services/restaurant';
import { locationsService } from '../../services/locationsService';

// Friendly labels for the API's derived `type` bucket and the raw `kind` field.
// These live outside the component, so they hold translation KEYS and are
// resolved with t() at render time.
const TYPE_LABEL_KEYS = {
  dish: 'menus.dishes.type.dish',
  beverage: 'menus.dishes.type.beverage',
  addon: 'menus.dishes.type.addon',
  combo: 'menus.dishes.type.combo',
  store_bought: 'menus.dishes.type.store_bought',
  bakery: 'menus.dishes.type.bakery',
  liquor: 'menus.dishes.type.liquor',
};

// The item types you can add: this catalogue is the single place that chooses kind/dish_type, fixed thereafter.
// Wording is shared with the dish editor's type selector, so it reuses those keys.
const ADD_MENU_TYPES = [
  { kind: 'dish',  dishType: 'food',     labelKey: 'addDish.typeSelector.dish.label',     descKey: 'addDish.typeSelector.dish.desc',     Icon: UtensilsCrossed, badge: 'bg-rose-100 text-rose-700' },
  { kind: 'dish',  dishType: 'beverage', labelKey: 'addDish.typeSelector.beverage.label', descKey: 'addDish.typeSelector.beverage.desc', Icon: Coffee,          badge: 'bg-sky-100 text-sky-700' },
  { kind: 'addon', dishType: 'food',     labelKey: 'addDish.typeSelector.addon.label',    descKey: 'addDish.typeSelector.addon.desc',    Icon: PlusCircle,      badge: 'bg-emerald-100 text-emerald-700' },
  { kind: 'combo', dishType: 'food',     labelKey: 'addDish.typeSelector.combo.label',    descKey: 'addDish.typeSelector.combo.desc',    Icon: Package,         badge: 'bg-violet-100 text-violet-700' },
  { kind: 'dish',  dishType: 'store_bought', labelKey: 'addDish.typeSelector.store_bought.label', descKey: 'addDish.typeSelector.store_bought.desc', Icon: ShoppingBag, badge: 'bg-amber-100 text-amber-700' },
  { kind: 'dish',  dishType: 'bakery',       labelKey: 'addDish.typeSelector.bakery.label',       descKey: 'addDish.typeSelector.bakery.desc',       Icon: Croissant,   badge: 'bg-orange-100 text-orange-700' },
  { kind: 'dish',  dishType: 'liquor',       labelKey: 'addDish.typeSelector.liquor.label',       descKey: 'addDish.typeSelector.liquor.desc',       Icon: Wine,        badge: 'bg-purple-100 text-purple-700' },
];

function AllDishesList({ restaurantId: restaurantIdProp }) {
  const { formatCurrency, formatDate, formatTime } = useAppSettings();
  const { t } = useT();
  const navigate = useNavigate();
  const { restaurantId: urlRestaurantId } = useParams();
  const { restaurantId: contextRestaurantId, locationId, location } = useRestaurant();
  const { user, hasPermission, isPermissionGated } = useAuth();
  // View-only roles see the catalogue and each dish's ingredients and recipe, but no actions.
  const canEditDishes = !isPermissionGated('dishes.edit') || hasPermission('dishes.edit');
  
  // Get restaurant from localStorage
  const storedRestaurant = getRestaurantFromStorage();
  const storedRestaurantId = storedRestaurant?.id;
  
  // Get restaurant from logged-in user
  const userRestaurantId = user?.restaurant_id || user?.restaurant;
  
  // Prefer the authenticated restaurant over the URL param, so a stale route can't force the wrong one.
  const restaurantId = restaurantIdProp || contextRestaurantId || userRestaurantId || storedRestaurantId || urlRestaurantId;
  
  console.log('AllDishesList - Restaurant ID Sources:', {
    prop: restaurantIdProp,
    url: urlRestaurantId,
    context: contextRestaurantId,
    user: userRestaurantId,
    stored: storedRestaurantId,
    final: restaurantId,
    userObject: user
  });
  
  const [dishes, setDishes] = useState([]);
  const [filteredDishes, setFilteredDishes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  // Every branch of this restaurant, so multi-location users get the "deactivate at which branch?" picker.
  const [locations, setLocations] = useState([]);
  const isMultiLocation = locations.length > 1;
  // The dish whose availability picker is open, its working off-branch set, and the in-flight save flag.
  const [availDish, setAvailDish] = useState(null);
  const [availOff, setAvailOff] = useState(() => new Set());
  const [availSaving, setAvailSaving] = useState(false);

  // Filter states, persisted for the browser session so returning from a dish keeps them as they were.
  const FILTERS_KEY = 'allDishesFilters';
  const [savedFilters] = useState(() => {
    try {
      return JSON.parse(sessionStorage.getItem(FILTERS_KEY) || '{}');
    } catch {
      return {};
    }
  });
  const [searchTerm, setSearchTerm] = useState(savedFilters.searchTerm ?? '');
  const [showActive, setShowActive] = useState(savedFilters.showActive ?? true);
  const [showInactive, setShowInactive] = useState(savedFilters.showInactive ?? true);
  // 'none' | 'category' | 'type' — how the catalogue is grouped on screen.
  const [groupBy, setGroupBy] = useState(savedFilters.groupBy ?? 'none');
  // 'all' or one type bucket (dish / beverage / addon / …) — show only that type.
  const [typeFilter, setTypeFilter] = useState(savedFilters.typeFilter ?? 'all');
  // Column sort: key is 'name' | 'type' | 'created'; dir is 'asc' | 'desc'.
  const [sortKey, setSortKey] = useState(savedFilters.sortKey ?? 'name');
  const [sortDir, setSortDir] = useState(savedFilters.sortDir ?? 'asc');
  useEffect(() => {
    sessionStorage.setItem(FILTERS_KEY, JSON.stringify({
      searchTerm, showActive, showInactive, groupBy, typeFilter, sortKey, sortDir,
    }));
  }, [searchTerm, showActive, showInactive, groupBy, typeFilter, sortKey, sortDir]);
  // The dish whose read-only details panel is open (null = closed).
  const [viewDish, setViewDish] = useState(null);
  // Dish ids whose inline variants row is expanded (the Variants-count dropdown).
  const [expandedRows, setExpandedRows] = useState(() => new Set());
  // Add Menu Item dropdown (Dish / Beverage / Add-on / Combo).
  const [addMenuOpen, setAddMenuOpen] = useState(false);

  // Load dishes on mount and whenever restaurant or location changes, so availability flags stay right.
  useEffect(() => {
    console.log('AllDishesList: useEffect triggered - restaurantId:', restaurantId);
    if (restaurantId) {
      console.log('AllDishesList: Triggering loadDishes()');
      loadDishes();
    } else {
      console.warn('AllDishesList: No restaurantId available, skipping API call');
    }
    // eslint-disable-next-line
  }, [restaurantId, locationId]);

  // Apply filters when dishes or filter criteria change
  useEffect(() => {
    console.log('AllDishesList: Applying filters to', dishes.length, 'dishes');
    applyFilters();
    // eslint-disable-next-line
  }, [dishes, searchTerm, showActive, showInactive, sortKey, sortDir, typeFilter]);

  // Load the branches, to know whether to offer the per-location picker or the single context toggle.
  useEffect(() => {
    if (!restaurantId) return;
    let cancelled = false;
    locationsService.list(restaurantId)
      .then((locs) => { if (!cancelled) setLocations((locs || []).filter((l) => l.is_active !== false)); })
      .catch(() => { if (!cancelled) setLocations([]); });
    return () => { cancelled = true; };
  }, [restaurantId]);

  const loadDishes = async () => {
    if (!restaurantId) {
      console.error('AllDishesList: restaurantId is not set');
      console.log('AllDishesList: All sources are null - prop, url, context, and localStorage');
      return;
    }
    
    console.log('AllDishesList: Loading dishes for restaurant ID:', restaurantId);
    setIsLoading(true);
    
    try {
      console.log('AllDishesList: Calling getDishes API...');
      const data = await getDishes(restaurantId, locationId);
      console.log('AllDishesList: API Response:', data);
      
      const dishArray = Array.isArray(data) ? data : [];
      console.log('AllDishesList: Processed dishes count:', dishArray.length);
      
      setDishes(dishArray);
      
      toast({
        title: 'Success',
        description: `Loaded ${dishArray.length} dishes`,
      });
    } catch (error) {
      console.error('AllDishesList: Error loading dishes:', error);
      setDishes([]);
      
      toast({
        title: 'Error',
        description: error.error || error.message || 'Failed to load dishes',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...dishes];

    // Filter by search term
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(dish => 
        dish.name.toLowerCase().includes(search) ||
        dish.description?.toLowerCase().includes(search) ||
        dish.category_name?.toLowerCase().includes(search)
      );
    }

    // Show only one type bucket (Add-ons, Beverages, …) when picked.
    if (typeFilter !== 'all') {
      filtered = filtered.filter((dish) => dishTypeKey(dish) === typeFilter);
    }

    // Filter by active/inactive status
    if (!showActive || !showInactive) {
      filtered = filtered.filter(dish => {
        if (showActive && dish.is_available) return true;
        if (showInactive && !dish.is_available) return true;
        return false;
      });
    }

    // Sort by the chosen column, over a stable copy so grouping order stays predictable.
    const dir = sortDir === 'asc' ? 1 : -1;
    filtered = [...filtered].sort((a, b) => {
      let cmp;
      if (sortKey === 'created') {
        cmp = (Date.parse(a.created_at) || 0) - (Date.parse(b.created_at) || 0);
      } else if (sortKey === 'type') {
        cmp = dishTypeKey(a).localeCompare(dishTypeKey(b)) || (a.name || '').localeCompare(b.name || '');
      } else {
        cmp = (a.name || '').localeCompare(b.name || '');
      }
      return cmp * dir;
    });

    setFilteredDishes(filtered);
  };

  // Normalised type bucket used for the Type badge and Type sort.
  const dishTypeKey = (dish) => dish.type
    || (dish.kind === 'addon' ? 'addon'
      : dish.kind === 'combo' ? 'combo'
        : ['beverage', 'store_bought', 'bakery', 'liquor'].includes(dish.dish_type)
          ? dish.dish_type : 'dish');

  // Header click: same column toggles direction, a new column resets to asc.
  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  // Expand/collapse a dish's inline variants dropdown in the catalogue table.
  const toggleExpanded = (dishId) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(dishId)) next.delete(dishId); else next.add(dishId);
      return next;
    });
  };

  const handleEditDish = (dish) => {
    console.log('AllDishesList: Navigating to edit dish', dish.id);
    navigate(`/restaurant/${restaurantId}/dish/${dish.id}/edit`, {
      state: { 
        dish,
        dishType: dish.dish_type || 'food'
      }
    });
  };

  // Only drafts can be deleted, since they never reach a live menu; finalised dishes stay edit-only.
  const handleDeleteDish = async (dish) => {
    if (!(await confirm({ title: 'Delete draft?', description: `Delete draft "${dish.name}"? This can't be undone.`, confirmText: 'Delete', tone: 'destructive' }))) return;
    try {
      await deleteDish(restaurantId, dish.id);
      toast({ title: 'Draft deleted', description: `"${dish.name}" was removed.` });
      loadDishes();
    } catch (err) {
      const msg = err?.error || err?.message || 'Could not delete the draft.';
      toast({ title: 'Delete failed', description: msg, variant: 'destructive' });
    }
  };

  // The "86" toggle for this branch: optimistic, rolling back if the API rejects it.
  const handleToggleLocationAvailability = async (dish) => {
    if (!locationId) {
      toast({
        title: 'No location selected',
        description: 'Pick a location first to change availability for this branch.',
        variant: 'destructive',
      });
      return;
    }
    const next = !(dish.available_here !== false);
    setDishes((prev) => prev.map((d) => (d.id === dish.id ? { ...d, available_here: next } : d)));
    try {
      await setDishLocationAvailability(restaurantId, locationId, dish.id, next);
      toast({
        title: next ? 'Available here' : 'Unavailable here',
        description: `"${dish.name}" is now ${next ? 'on' : 'off'} the ordering menu at ${location?.name || 'this location'}.`,
      });
    } catch (err) {
      // Roll back the optimistic flip.
      setDishes((prev) => prev.map((d) => (d.id === dish.id ? { ...d, available_here: !next } : d)));
      const msg = err?.error || err?.message || 'Could not change availability.';
      toast({ title: 'Update failed', description: msg, variant: 'destructive' });
    }
  };

  // Flip the dish-level Active/Inactive status from the list; deactivating confirms first, since it hits every branch.
  const handleToggleActive = async (dish) => {
    const next = !dish.is_available;
    if (!next) {
      const ok = await confirm({
        title: 'Deactivate item?',
        description: `"${dish.name}" will be marked Inactive and removed from every menu (at all locations) until you reactivate it.`,
        confirmText: 'Deactivate',
        tone: 'destructive',
      });
      if (!ok) return;
    }
    setDishes((prev) => prev.map((d) => (d.id === dish.id ? { ...d, is_available: next } : d)));
    try {
      await setDishActive(restaurantId, dish.id, next);
      toast({
        title: next ? 'Item activated' : 'Item deactivated',
        description: `"${dish.name}" is now ${next ? 'Active' : 'Inactive'}.`,
      });
    } catch (err) {
      setDishes((prev) => prev.map((d) => (d.id === dish.id ? { ...d, is_available: !next } : d)));
      const msg = err?.error || err?.message || 'Could not change status.';
      toast({ title: 'Update failed', description: msg, variant: 'destructive' });
    }
  };

  // Status-column deactivate/reactivate; multi-branch restaurants get the per-location picker as the confirmation.
  const requestToggleAvailability = async (dish) => {
    if (isMultiLocation) { openAvailability(dish); return; }
    const availableHere = dish.available_here !== false;
    if (availableHere) {
      const ok = await confirm({
        title: 'Deactivate item?',
        description: `"${dish.name}" will be turned off on the ordering screen${location?.name ? ` at ${location.name}` : ' here'}. It stays on every menu and you can reactivate it anytime.`,
        confirmText: 'Deactivate',
        tone: 'destructive',
      });
      if (!ok) return;
    }
    handleToggleLocationAvailability(dish);
  };

  // Open the multi-location availability picker for a dish, seeded with the branches it is switched off at.
  const openAvailability = (dish) => {
    setAvailDish(dish);
    setAvailOff(new Set(dish.off_locations || []));
  };

  const toggleAvailLoc = (locId) => {
    setAvailOff((prev) => {
      const next = new Set(prev);
      if (next.has(locId)) next.delete(locId); else next.add(locId);
      return next;
    });
  };

  const toggleAvailAll = () => {
    setAvailOff((prev) => (
      prev.size === locations.length ? new Set() : new Set(locations.map((l) => l.id))
    ));
  };

  // Persist the whole off-set in one call, then sync the row so its badge updates without a full reload.
  const saveAvailability = async () => {
    if (!availDish) return;
    const offIds = [...availOff];
    setAvailSaving(true);
    try {
      await setDishLocationAvailabilityBulk(restaurantId, availDish.id, offIds);
      setDishes((prev) => prev.map((d) => (
        d.id === availDish.id
          ? { ...d, off_locations: offIds, available_here: !(locationId && offIds.includes(locationId)) }
          : d
      )));
      const offCount = offIds.length;
      toast({
        title: 'Availability updated',
        description: offCount === 0
          ? `"${availDish.name}" is available at every location.`
          : `"${availDish.name}" is deactivated at ${offCount} of ${locations.length} location${locations.length === 1 ? '' : 's'}.`,
      });
      setAvailDish(null);
    } catch (err) {
      const msg = err?.error || err?.message || 'Could not update availability.';
      toast({ title: 'Update failed', description: msg, variant: 'destructive' });
    } finally {
      setAvailSaving(false);
    }
  };

  const handleAddDish = (kind = 'dish', dishType = 'food') => {
    console.log('AllDishesList: Navigating to add dish:', { kind, dishType });
    navigate(`/restaurant/${restaurantId}/dish/add`, {
      state: { kind, dishType }
    });
  };

  const getStatusBadge = (dish) => {
    // Drafts are inactive too, so check the draft flag first and give them their own amber badge.
    if (dish.is_draft) {
      return <Badge className="bg-amber-50 text-amber-800 border-amber-300 hover:bg-amber-50">Draft</Badge>;
    }
    if (dish.is_available) {
      return <Badge className="bg-success-soft text-success border-success/30 hover:bg-success-soft">● Active</Badge>;
    }
    return <Badge variant="secondary">Inactive</Badge>;
  };

  const getDishTypeBadge = (dish) => {
    // Prefer the API's derived `type`, falling back to kind / dish_type so add-ons and combos aren't mislabelled.
    const type = dish.type
      || (dish.kind === 'addon' ? 'addon'
        : dish.kind === 'combo' ? 'combo'
          : ['beverage', 'store_bought', 'bakery', 'liquor'].includes(dish.dish_type)
            ? dish.dish_type : 'dish');
    if (type === 'beverage') {
      return <Badge variant="outline" className="bg-info-soft text-info border-info/30">Beverage</Badge>;
    }
    if (type === 'addon') {
      return <Badge variant="outline" className="bg-emerald-100 text-emerald-700 border-emerald-300">Add-on</Badge>;
    }
    if (type === 'combo') {
      return <Badge variant="outline" className="bg-violet-100 text-violet-700 border-violet-300">Combo</Badge>;
    }
    if (type === 'store_bought') {
      return <Badge variant="outline" className="bg-amber-100 text-amber-700 border-amber-300">Store-bought</Badge>;
    }
    if (type === 'bakery') {
      return <Badge variant="outline" className="bg-orange-100 text-orange-700 border-orange-300">Bakery</Badge>;
    }
    if (type === 'liquor') {
      return <Badge variant="outline" className="bg-purple-100 text-purple-700 border-purple-300">Liquor</Badge>;
    }
    return <Badge variant="outline" className="bg-accent text-accent-foreground border-primary/20">Dish</Badge>;
  };

  // Build on-screen sections when grouping is active, by category master or by the derived type bucket.
  const groupedSections = React.useMemo(() => {
    if (groupBy === 'none') return null;
    const map = new Map();
    filteredDishes.forEach((dish) => {
      let label;
      if (groupBy === 'category') {
        label = dish.category_master_name || dish.category_name
          || t('menus.dishes.group.uncategorized');
      } else {
        const key = TYPE_LABEL_KEYS[dish.type] || TYPE_LABEL_KEYS[dish.kind];
        label = key ? t(key)
          : (dish.dish_type ? dish.dish_type.replace(/_/g, ' ') : t('menus.dishes.group.other'));
      }
      if (!map.has(label)) map.set(label, []);
      map.get(label).push(dish);
    });
    return [...map.entries()]
      .map(([label, items]) => ({ label, items }))
      .sort((a, b) => a.label.localeCompare(b.label));
    // `t` is a dependency: the group headings ARE translated text, so
    // switching language has to rebuild them rather than leave the old
    // language's headings sitting above the rows.
  }, [filteredDishes, groupBy, t]);

  // Per-location availability summary for the Status column and View panel; null when there is nothing to show.
  const availabilitySummary = (dish) => {
    if (dish.is_draft) return null;
    if (isMultiLocation) {
      const offCount = (dish.off_locations || []).length;
      return offCount === 0
        ? `Available at all ${locations.length} locations`
        : `Deactivated at ${offCount} of ${locations.length}`;
    }
    if (locationId) {
      return dish.available_here !== false
        ? `Available${location?.name ? ` at ${location.name}` : ' here'}`
        : `Deactivated${location?.name ? ` at ${location.name}` : ' here'}`;
    }
    return null;
  };

  // One <tr> for the catalogue table. `serial` is the 1-based row number.
  const renderDishTableRow = (dish, serial) => {
    const variantCount = dish.variants?.length || 0;
    const canDeactivate = !dish.is_draft && (isMultiLocation || locationId);
    const availableHere = dish.available_here !== false;
    const summary = availabilitySummary(dish);
    const isExpanded = expandedRows.has(dish.id);
    return (
      <React.Fragment key={dish.id}>
      <tr className={`border-b border-border hover:bg-accent/40 transition-colors ${isExpanded ? 'bg-accent/30' : 'last:border-0'}`}>
        {/* Serial number */}
        <td className="px-3 py-3 text-sm text-muted-foreground font-mono tabular align-top w-12">{serial}</td>

        {/* Item: thumbnail + name + category */}
        <td className="px-3 py-3 align-top">
          <div className="flex items-start gap-3">
            {dish.image ? (
              <img
                src={dish.image}
                alt=""
                loading="lazy"
                className="h-11 w-11 rounded-lg object-cover border border-border shrink-0 cursor-pointer"
                onClick={() => setViewDish(dish)}
              />
            ) : (
              <div className="h-11 w-11 rounded-lg bg-muted text-muted-foreground/50 flex items-center justify-center shrink-0">
                <UtensilsCrossed className="h-4 w-4" />
              </div>
            )}
            <div className="min-w-0">
              <div className="font-medium text-foreground">{dish.name}</div>
              {dish.category_master_name && (
                <div className="text-xs text-muted-foreground mt-0.5">{dish.category_master_name}</div>
              )}
            </div>
          </div>
        </td>

        {/* Type */}
        <td className="px-3 py-3 align-top">{getDishTypeBadge(dish)}</td>

        {/* Variants count — click to expand an inline dropdown of variants + prices. */}
        <td className="px-3 py-3 align-top text-sm font-mono tabular text-center">
          {variantCount > 0 ? (
            <button
              type="button"
              onClick={() => toggleExpanded(dish.id)}
              aria-expanded={isExpanded}
              title={isExpanded ? 'Hide variants' : 'Show variants & prices'}
              className="inline-flex items-center gap-1 rounded-md px-2 py-1 transition-colors hover:bg-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <span className="font-semibold text-foreground">{variantCount}</span>
              <ChevronDown className={`h-3.5 w-3.5 text-muted-foreground transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
            </button>
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
        </td>

        {/* Created date + time */}
        <td className="px-3 py-3 align-top text-sm text-muted-foreground whitespace-nowrap">
          {dish.created_at ? (
            <>
              <div>{formatDate(dish.created_at)}</div>
              <div className="text-xs">{formatTime(dish.created_at)}</div>
            </>
          ) : '—'}
        </td>

        {/* Status: the clickable lifecycle toggle plus the availability summary; drafts aren't toggleable. */}
        <td className="px-3 py-3 align-top">
          <div className="flex flex-col gap-1 items-start">
            {dish.is_draft ? (
              getStatusBadge(dish)
            ) : (
              <button
                type="button"
                onClick={() => handleToggleActive(dish)}
                className="rounded-full transition-opacity hover:opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                title={dish.is_available
                  ? 'Active — click to deactivate (removes from every menu)'
                  : 'Inactive — click to activate'}
              >
                {getStatusBadge(dish)}
              </button>
            )}
            {locationId && dish.available_here === false && (
              <Badge className="bg-rose-50 text-rose-700 border-rose-300 hover:bg-rose-50">Off here</Badge>
            )}
            {summary && (
              <span className="text-[11px] text-muted-foreground">{summary}</span>
            )}
          </div>
        </td>

        {/* Actions: View · Edit · Deactivate/Reactivate · Delete (drafts) */}
        <td className="px-3 py-3 align-top">
          <div className="flex items-center justify-end gap-1.5 flex-wrap">
            <Button onClick={() => setViewDish(dish)} variant="ghost" size="sm" title="View details">
              <Eye className="h-4 w-4 sm:mr-1" />
              <span className="hidden sm:inline">View</span>
            </Button>
            {canEditDishes && (
              <Button onClick={() => handleEditDish(dish)} variant="outline" size="sm" title="Edit item">
                <Edit className="h-4 w-4 sm:mr-1" />
                <span className="hidden sm:inline">Edit</span>
              </Button>
            )}
            {canEditDishes && canDeactivate && (
              <Button
                onClick={() => requestToggleAvailability(dish)}
                variant="outline"
                size="sm"
                className={availableHere
                  ? 'text-rose-600 border-rose-200 hover:bg-rose-50 hover:text-rose-700'
                  : 'text-emerald-700 border-emerald-200 hover:bg-emerald-50 hover:text-emerald-800'}
                title={isMultiLocation ? 'Manage availability by location' : (availableHere ? 'Deactivate at this location' : 'Reactivate at this location')}
              >
                {isMultiLocation
                  ? <><MapPin className="h-4 w-4 sm:mr-1" /><span className="hidden sm:inline">Availability</span></>
                  : availableHere
                    ? <><PowerOff className="h-4 w-4 sm:mr-1" /><span className="hidden sm:inline">Deactivate</span></>
                    : <><Power className="h-4 w-4 sm:mr-1" /><span className="hidden sm:inline">Reactivate</span></>}
              </Button>
            )}
            {canEditDishes && dish.is_draft && (
              <Button
                onClick={() => handleDeleteDish(dish)}
                variant="outline"
                size="sm"
                className="text-rose-600 border-rose-200 hover:bg-rose-50 hover:text-rose-700"
                title="Delete this draft"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        </td>
      </tr>

      {/* Inline variants dropdown — revealed when the Variants count is clicked. */}
      {isExpanded && variantCount > 0 && (
        <tr className="border-b border-border last:border-0 bg-muted/30">
          <td />
          <td colSpan={6} className="px-3 pb-3 pt-0 align-top">
            <div className="rounded-md border border-border bg-card divide-y divide-border/60">
              {dish.variants.map((variant, idx) => (
                <div key={variant.id ?? idx} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                  <span className="text-foreground/80 min-w-0 truncate">
                    {variant.variant_name || 'Standard'}
                    {variant.quantity && variant.unit && (
                      <span className="text-xs text-muted-foreground ml-1.5 font-mono">
                        ({formatUnitLabel(variant.quantity, variant.unit)})
                      </span>
                    )}
                    {variant.is_free && (
                      <Badge variant="outline" className="ml-2 text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">Free</Badge>
                    )}
                  </span>
                  <span className="font-mono font-semibold text-primary tabular shrink-0">
                    {variant.is_free ? 'Free' : formatCurrency(variant.price)}
                  </span>
                </div>
              ))}
            </div>
          </td>
        </tr>
      )}
      </React.Fragment>
    );
  };

  // A clickable, sortable column header, neutral until active and then showing the current direction.
  const SortableTh = ({ sortByKey, children, className = '' }) => {
    const active = sortKey === sortByKey;
    const Icon = !active ? ChevronsUpDown : sortDir === 'asc' ? ChevronUp : ChevronDown;
    return (
      <th className={`px-3 py-2 font-medium ${className}`}>
        <button
          type="button"
          onClick={() => toggleSort(sortByKey)}
          className={`inline-flex items-center gap-1 uppercase tracking-wider transition-colors hover:text-foreground ${active ? 'text-foreground' : ''}`}
          title={`Sort by ${sortByKey}`}
        >
          {children}
          <Icon className={`h-3 w-3 ${active ? '' : 'opacity-40'}`} />
        </button>
      </th>
    );
  };

  // The catalogue table itself; `startSerial` lets grouped sections continue the numbering.
  const renderDishTable = (items, startSerial = 1) => (
    <div className="overflow-x-auto rounded-lg border border-border">
      <table className="w-full border-collapse text-left">
        <thead>
          <tr className="border-b border-border bg-muted/40 text-[11px] uppercase tracking-wider text-muted-foreground">
            <th className="px-3 py-2 font-medium w-12">#</th>
            <SortableTh sortByKey="name">Item</SortableTh>
            <SortableTh sortByKey="type">Type</SortableTh>
            <th className="px-3 py-2 font-medium text-center">Variants</th>
            <SortableTh sortByKey="created">Created</SortableTh>
            <th className="px-3 py-2 font-medium">Status</th>
            <th className="px-3 py-2 font-medium text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {items.map((dish, idx) => renderDishTableRow(dish, startSerial + idx))}
        </tbody>
      </table>
    </div>
  );

  // Read-only details panel opened by the View action.
  const renderViewDishDialog = () => (
    <Dialog open={!!viewDish} onOpenChange={(open) => { if (!open) setViewDish(null); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{viewDish?.name}</DialogTitle>
        </DialogHeader>
        {viewDish && (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2">
              {getStatusBadge(viewDish)}
              {getDishTypeBadge(viewDish)}
            </div>
            {viewDish.image && (
              <div className="w-full h-44 bg-secondary rounded-md overflow-hidden">
                <img src={viewDish.image} alt={viewDish.name} className="w-full h-full object-cover" />
              </div>
            )}
            <dl className="grid grid-cols-3 gap-x-3 gap-y-2 text-sm">
              {viewDish.category_master_name && (
                <>
                  <dt className="text-muted-foreground">Category</dt>
                  <dd className="col-span-2 text-foreground">{viewDish.category_master_name}</dd>
                </>
              )}
              {viewDish.created_at && (
                <>
                  <dt className="text-muted-foreground">Created</dt>
                  <dd className="col-span-2 text-foreground">{formatDate(viewDish.created_at)} {formatTime(viewDish.created_at)}</dd>
                </>
              )}
              {viewDish.description && (
                <>
                  <dt className="text-muted-foreground">Description</dt>
                  <dd className="col-span-2 text-foreground">{viewDish.description}</dd>
                </>
              )}
              {availabilitySummary(viewDish) && (
                <>
                  <dt className="text-muted-foreground">Availability</dt>
                  <dd className="col-span-2 text-foreground">{availabilitySummary(viewDish)}</dd>
                </>
              )}
            </dl>
            <div>
              <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-mono font-semibold mb-2">
                Variants ({viewDish.variants?.length || 0})
              </p>
              {viewDish.variants?.length ? (
                <div className="space-y-1">
                  {viewDish.variants.map((variant, idx) => (
                    <div key={idx} className="flex items-center justify-between text-sm border-b border-border/60 last:border-0 py-1">
                      <span className="text-foreground/80">
                        {variant.variant_name || 'Standard'}
                        {variant.quantity && variant.unit && (
                          <span className="text-xs text-muted-foreground ml-1 font-mono">
                            ({formatUnitLabel(variant.quantity, variant.unit)})
                          </span>
                        )}
                      </span>
                      <span className="font-mono font-semibold text-primary tabular">
                        {formatCurrency(variant.price)}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No variants.</p>
              )}
            </div>

            {/* Kitchen reference: ingredients come from the dish, exact amounts from each variant's recipe. */}
            {(viewDish.ingredients?.length || viewDish.allergen_tags?.length) ? (
              <div>
                <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-mono font-semibold mb-2">
                  Ingredients ({viewDish.ingredients?.length || 0})
                </p>
                {viewDish.ingredients?.length ? (
                  <div className="flex flex-wrap gap-1.5">
                    {viewDish.ingredients.map((ing) => (
                      <span key={ing.id} className="text-xs rounded-full bg-secondary px-2.5 py-1 text-foreground/80">
                        {ing.name}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">No ingredients recorded.</p>
                )}
                {viewDish.allergen_tags?.length > 0 && (
                  <p className="mt-2 text-xs text-rose-600">
                    <strong>Allergens:</strong> {viewDish.allergen_tags.map((a) => a.name).join(', ')}
                  </p>
                )}
              </div>
            ) : null}

            {viewDish.variants?.some((v) => v.recipe?.length) && (
              <div>
                <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-mono font-semibold mb-2">
                  Recipe (per variant)
                </p>
                <div className="space-y-2">
                  {viewDish.variants.filter((v) => v.recipe?.length).map((variant, idx) => {
                    const ingName = (id) =>
                      viewDish.ingredients?.find((i) => i.id === id)?.name || `Ingredient #${id}`;
                    return (
                      <div key={idx} className="text-sm border border-border/60 rounded-md px-3 py-2">
                        <p className="font-medium text-foreground/90 mb-1">{variant.variant_name || 'Standard'}</p>
                        <ul className="space-y-0.5">
                          {variant.recipe.map((row, i) => (
                            <li key={i} className="flex justify-between text-foreground/70">
                              <span>{ingName(row.ingredient_id)}</span>
                              <span className="font-mono text-xs">{row.quantity} {row.unit}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => setViewDish(null)}>Close</Button>
          {viewDish && canEditDishes && (
            <Button onClick={() => { const d = viewDish; setViewDish(null); handleEditDish(d); }}>
              <Edit className="h-4 w-4 mr-1" /> Edit
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  if (!restaurantId) {
    return (
      <div className="w-full p-6">
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-gray-500 text-lg mb-4">
              No restaurant selected. Please select a restaurant first.
            </p>
            <p className="text-sm text-gray-400">
              Debug Info: {JSON.stringify({
                userRestaurantId: user?.restaurant_id || user?.restaurant,
                storedRestaurantId: getRestaurantFromStorage()?.id,
                userEmail: user?.email
              }, null, 2)}
            </p>
            <Button 
              onClick={() => navigate('/restaurant-setup')}
              className="mt-4"
            >
              Go to Restaurant Setup
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="w-full p-6 space-y-6">
      {/* Header */}
      <div className="flex items-end justify-between">
        <div>
          <h2 className="font-display text-4xl font-semibold tracking-tight text-foreground leading-none">All Menu Items</h2>
          <p className="text-sm text-muted-foreground mt-2 font-mono tabular">
            {filteredDishes.length} of {dishes.length} dishes
          </p>
        </div>
        <div className="flex gap-2">
          {canEditDishes && (
          <div className="relative">
            <Button
              onClick={() => setAddMenuOpen((o) => !o)}
              aria-haspopup="menu"
              aria-expanded={addMenuOpen}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Menu Item
              <ChevronDown className="h-4 w-4 ml-2 -mr-1 opacity-80" />
            </Button>
            {addMenuOpen && (
              <>
                {/* Click-away backdrop closes the menu. */}
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setAddMenuOpen(false)}
                  aria-hidden="true"
                />
                <div
                  role="menu"
                  className="absolute right-0 z-50 mt-2 w-64 rounded-lg border border-border bg-card p-1 shadow-lg"
                >
                  {ADD_MENU_TYPES.map((opt) => (
                    <button
                      key={`${opt.kind}-${opt.dishType}`}
                      type="button"
                      role="menuitem"
                      onClick={() => {
                        setAddMenuOpen(false);
                        handleAddDish(opt.kind, opt.dishType);
                      }}
                      className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-left transition-colors hover:bg-accent"
                    >
                      <span className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md ${opt.badge}`}>
                        <opt.Icon className="h-4 w-4" />
                      </span>
                      <span className="min-w-0">
                        <span className="block text-sm font-medium text-foreground">{opt.label}</span>
                      </span>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
          )}
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            {/* Search */}
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="search">Search Dishes</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  id="search"
                  placeholder="Search by name, description, or category..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            {/* Show only one type */}
            <div className="space-y-2">
              <Label htmlFor="type-filter">Show Only</Label>
              <select
                id="type-filter"
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              >
                <option value="all">{t('menus.dishes.filter.allTypes')}</option>
                {Object.entries(TYPE_LABEL_KEYS).map(([key, labelKey]) => (
                  <option key={key} value={key}>{t(labelKey)}</option>
                ))}
              </select>
            </div>

            {/* Group By */}
            <div className="space-y-2">
              <Label htmlFor="group-by">Group By</Label>
              <select
                id="group-by"
                value={groupBy}
                onChange={(e) => setGroupBy(e.target.value)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              >
                <option value="none">No grouping</option>
                <option value="category">Category</option>
                <option value="type">Type (dish / beverage / add-on / …)</option>
              </select>
            </div>

            {/* Status Filters */}
            <div className="space-y-2">
              <Label>Status Filter</Label>
              <div className="flex items-center gap-6 pt-2">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="show-active"
                    checked={showActive}
                    onCheckedChange={setShowActive}
                  />
                  <Label htmlFor="show-active" className="cursor-pointer font-normal">
                    Show Active
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="show-inactive"
                    checked={showInactive}
                    onCheckedChange={setShowInactive}
                  />
                  <Label htmlFor="show-inactive" className="cursor-pointer font-normal">
                    Show Inactive
                  </Label>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Dishes table */}
      {isLoading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading dishes…</p>
        </div>
      ) : filteredDishes.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">
              {dishes.length === 0
                ? 'No dishes found. Add your first dish to get started!'
                : 'No dishes match your filters.'}
            </p>
          </CardContent>
        </Card>
      ) : groupBy === 'none' ? (
        renderDishTable(filteredDishes)
      ) : (
        <div className="space-y-8">
          {/* Serial numbers run continuously across groups. */}
          {(() => { let running = 0; return groupedSections.map((section) => {
            const start = running + 1;
            running += section.items.length;
            return (
              <div key={section.label}>
                <div className="flex items-center gap-3 mb-3">
                  <h3 className="font-display text-xl font-semibold tracking-tight text-foreground">
                    {section.label}
                  </h3>
                  <span className="text-xs font-mono text-muted-foreground tabular">
                    {section.items.length} item{section.items.length !== 1 ? 's' : ''}
                  </span>
                  <div className="flex-1 h-px bg-border" />
                </div>
                {renderDishTable(section.items, start)}
              </div>
            );
          }); })()}
        </div>
      )}

      {/* Read-only details panel (View action). */}
      {renderViewDishDialog()}

      {/* Per-location availability picker: tick a branch to switch the dish OFF there, posting the whole state on Save. */}
      <Dialog open={!!availDish} onOpenChange={(open) => { if (!open) setAvailDish(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Availability by location</DialogTitle>
            <DialogDescription>
              {availDish ? <>Choose where <strong>{availDish.name}</strong> is deactivated. Ticked branches are switched off; it still stays on every menu.</> : null}
            </DialogDescription>
          </DialogHeader>

          {locations.length > 0 && (
            <button
              type="button"
              onClick={toggleAvailAll}
              className="self-start text-xs font-medium text-primary underline-offset-2 hover:underline"
            >
              {availOff.size === locations.length ? 'Clear all' : 'Deactivate at all locations'}
            </button>
          )}

          <div className="max-h-72 space-y-1 overflow-y-auto">
            {locations.map((loc) => {
              const off = availOff.has(loc.id);
              return (
                <button
                  key={loc.id}
                  type="button"
                  onClick={() => toggleAvailLoc(loc.id)}
                  className={`flex w-full cursor-pointer items-center justify-between gap-3 rounded-md border px-3 py-2 text-left text-sm transition-colors ${
                    off ? 'border-rose-200 bg-rose-50' : 'border-border bg-card hover:bg-accent'
                  }`}
                >
                  <span className="flex items-center gap-2 min-w-0">
                    <MapPin className={`h-4 w-4 shrink-0 ${off ? 'text-rose-500' : 'text-muted-foreground'}`} />
                    <span className="truncate text-foreground">{loc.name}</span>
                    {loc.is_primary && (
                      <Badge variant="outline" className="text-[10px]">Primary</Badge>
                    )}
                  </span>
                  <span className="flex items-center gap-2 shrink-0">
                    <span className={`text-xs ${off ? 'text-rose-600 font-medium' : 'text-muted-foreground'}`}>
                      {off ? 'Deactivated' : 'Available'}
                    </span>
                    <Checkbox checked={off} className="pointer-events-none" />
                  </span>
                </button>
              );
            })}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAvailDish(null)} disabled={availSaving}>
              Cancel
            </Button>
            <Button onClick={saveAvailability} disabled={availSaving}>
              {availSaving ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}

export default AllDishesList;
